import React, { FC } from "react";
import { Fade, Popper, Typography } from "@mui/material";
import { Link } from "gatsby-theme-material-ui";

import { Colors } from "@theme/colors";
import HeaderMenu from "./menu";
import DropdownMenu from "./dropdown";
import { MenuLink } from "@src/types";
import {
  camelize,
  filterFooterNavItems,
  uiContainerTransitionMs,
  uiOpacityHover,
} from "@src/utils";

interface Props {
  isHome: boolean;
  reverseColor: boolean;
  link: MenuLink;
  arrowColor?: string;
}

const HeaderLink: FC<Props> = ({ isHome, reverseColor, link }) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [isOpen, setIsOpen] = React.useState<boolean>(false);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };
  const handleClose = () => {
    setAnchorEl(null);
    setIsOpen(false);
  };
  const handleHover = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    setIsOpen(true);
  };

  const textColor =
    isHome || reverseColor ? Colors.PrimaryContrast : Colors.PrimaryMain;

  let linkBase = "";

  try {
    linkBase = link.link.split("/")[1];
  } catch (e) {}

  const linkStyle = {
    display: "flex",
    alignItems: "center",
    color: textColor,
    fontSize: "15px",
    fontWeight: "600",
    lineHeight: "24px",
    textDecoration: "none",
    opacity: 1,
    transition: `opacity ${uiContainerTransitionMs} ease-in-out !important`,
    "&:hover": {
      opacity: uiOpacityHover,
    },
  };

  const linkId = `link-${camelize(link.name)}`;
  const filteredMenuLinks: MenuLink[] = link?.menuLinks
    ? link.menuLinks.filter(filterFooterNavItems)
    : [];

  if (link.type === "submenu" && link?.subSections?.length > 0) {
    return (
      <div onMouseEnter={handleHover} onMouseLeave={handleClose}>
        <Link
          id={linkId}
          href=""
          sx={{
            ...linkStyle,
            opacity: isOpen ? `${uiOpacityHover} !important` : 1,
            transition: `opacity ${uiContainerTransitionMs}ms ease-in-out`,
            "&:hover": {
              opacity: uiOpacityHover,
            },
          }}
          color={`${textColor} !important`}
          onClick={handleClick}
          underline="none"
        >
          {link.name}
        </Link>
        <Popper
          open={open}
          anchorEl={anchorEl}
          disablePortal={true}
          keepMounted={true}
          placement="bottom-start"
          sx={{
            maxWidth: link?.maxWidth ? link.maxWidth : "auto",
            width: link?.maxWidth ? "100%" : "auto",
          }}
          transition
        >
          {({ TransitionProps }) => (
            <Fade {...TransitionProps} timeout={uiContainerTransitionMs}>
              <div>
                <HeaderMenu
                  subSections={link.subSections}
                  containerType={link?.containerType}
                  arrowColor={
                    link?.containerType === "columns-services" ? "#B783EE" : ""
                  }
                />
              </div>
            </Fade>
          )}
        </Popper>
      </div>
    );
  } else if (link.type === "dropdown" && filteredMenuLinks.length > 0) {
    return (
      <div onMouseEnter={handleHover} onMouseLeave={handleClose}>
        <Link
          id={linkId}
          href=""
          onClick={handleClick}
          underline="none"
          sx={{
            ...linkStyle,
            opacity: isOpen ? `${uiOpacityHover} !important` : 1,
            transition: `opacity ${uiContainerTransitionMs}ms ease-in-out`,
            "&:hover": {
              opacity: uiOpacityHover,
            },
          }}
          color={`${textColor} !important`}
        >
          {link.name}
        </Link>
        <Popper
          open={open}
          anchorEl={anchorEl}
          disablePortal={true}
          keepMounted={true}
          placement="bottom-start"
          sx={{
            maxWidth: link?.maxWidth ? link.maxWidth : "auto",
            width: link?.maxWidth ? "100%" : "auto",
          }}
          transition
        >
          {({ TransitionProps }) => (
            <Fade {...TransitionProps} timeout={uiContainerTransitionMs}>
              <div>
                <DropdownMenu menuLinks={filteredMenuLinks} />
              </div>
            </Fade>
          )}
        </Popper>
      </div>
    );
  } else if (link.type === "internal") {
    return (
      <Link
        id={linkId}
        to={link.link}
        underline="none"
        color={`${textColor} !important`}
      >
        <Typography variant="body2" sx={linkStyle}>
          {link.name}
        </Typography>
      </Link>
    );
  } else if (link.type === "external") {
    return (
      <Link
        href={link.link}
        target="_blank"
        sx={linkStyle}
        underline="none"
        color={`${textColor} !important`}
      >
        {link.name}
      </Link>
    );
  } else {
    return (
      <Typography
        variant="body2"
        sx={linkStyle}
        color={`${textColor} !important`}
      >
        {link.name}
      </Typography>
    );
  }
};

export default HeaderLink;
